import React, { useCallback, useEffect, useState } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import OnboardingWizard from './OnboardingWizard';
import { useAuth } from '../contexts/AuthContext';
import { apiGet } from '../lib/api';

const ONBOARDING_DISMISS_KEY = 'ellora_onboarding_dismissed';

export default function Layout() {
  const { user, loading, checkAuth } = useAuth();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Auto-close the mobile drawer whenever route changes
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  const fetchProjects = useCallback(async () => {
    if (!user) return;
    try {
      const list = await apiGet('/projects');
      setProjects(Array.isArray(list) ? list : (list?.items || []));
    } catch (_) {}
  }, [user]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  // Expose a global refresh trigger so Employee Master can invalidate the
  // project list after saving project assignments (no logout needed).
  useEffect(() => {
    window.__refreshElloryProjects = fetchProjects;
    return () => { delete window.__refreshElloryProjects; };
  }, [fetchProjects]);

  // Poll for RBAC department-profile changes every 60 seconds.
  // When the version changes, re-fetch /auth/me so sidebar modules update
  // immediately without requiring a logout/login cycle.
  useEffect(() => {
    if (!user) return;
    let lastVersion = null;
    const poll = async () => {
      try {
        const r = await apiGet('/rbac/v2/rbac-version');
        if (lastVersion !== null && r.version !== lastVersion) {
          await checkAuth();
        }
        lastVersion = r.version;
      } catch (_) {}
    };
    poll();
    const interval = setInterval(poll, 60_000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  // respect the org-wide onboarding toggles set by super_admin.
  //
  // SUPER_ADMIN: shown if workspace is empty AND user hasn't dismissed it
  //              AND wizard_enabled_admin is true.
  // REGULAR USER: shown on EVERY login until ALL 4 mandatory steps are complete
  //               (profile fields, password, tour, HR policies) — unless
  //               wizard_enabled_users is set to false by super_admin.
  useEffect(() => {
    if (!user) return;
    const isSuper = user.role === 'super_admin';
    apiGet('/settings/onboarding').catch(() => ({})).then(cfg => {
      const adminOn = cfg.wizard_enabled_admin !== false;
      const usersOn = cfg.wizard_enabled_users !== false;

      if (isSuper) {
        if (!adminOn) return; // super_admin turned it off for themselves / org
        if (cfg.admin_wizard_dismissed === true) return; // dismissed server-side (persists across devices)
        if (localStorage.getItem(ONBOARDING_DISMISS_KEY) === '1') return;
        Promise.all([
          apiGet('/hr/employees').catch(() => []),
          apiGet('/projects').catch(() => []),
        ]).then(([emps, projs]) => {
          const eList = Array.isArray(emps) ? emps : (emps?.employees || []);
          const pList = Array.isArray(projs) ? projs : [];
          if (eList.length === 0 && pList.length === 0) setShowOnboarding(true);
        });
      } else {
        if (!usersOn) return; // wizard disabled org-wide for users
        Promise.all([
          apiGet('/hr/me').catch(() => null),
          apiGet('/hr/policies/me/pending').catch(() => ({ pending: [] })),
        ]).then(([me, pendRes]) => {
          const emp = me?.employee;
          const profileOk = !!(emp && emp.phone && emp.emergency_contact);
          // Password change is enforced at LOGIN (Login.jsx), so it is no longer
          // an onboarding-wizard gate.
          const policiesOk = (pendRes?.pending || []).length === 0;
          const tourOk = JSON.parse(localStorage.getItem('ellora_onboarding_status') || '{}').tour === true;
          if (!(profileOk && policiesOk && tourOk)) {
            setShowOnboarding(true);
          }
        });
      }
    });
  }, [user]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500 font-mono text-sm">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="h-screen flex bg-[#FDFDFD]">
      <Sidebar mobileOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
      <div className="flex-1 flex flex-col min-w-0">
        <Header
          projects={projects}
          activeProject={activeProject}
          setActiveProject={setActiveProject}
          onOpenMenu={() => setMobileMenuOpen(true)}
        />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8">
          <Outlet context={{ projects, activeProject }} />
        </main>
      </div>
      {showOnboarding && (
        <OnboardingWizard user={user} onClose={() => setShowOnboarding(false)} />
      )}
    </div>
  );
}
